/**
 * Due Dates Page
 *
 * Payment schedule and overdue invoice management.
 * Uses real data from the accounting store (invoices from database).
 */

import React, { useState, useMemo, useEffect } from 'react';
import type { DueDate, DueDateStatus } from '../../types/payment';
import { getDaysFromDueDate } from '../../types/payment';
import type { CentsAmount } from '@business-assistant/shared';
import { FastActions, type ActionType, type LinkedCounts } from '../components/FastActions';
import { useAccountingStore } from '../../state/accountingState';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface DueDatesPageProps {
  onNavigate?: (path: string) => void;
  onBack?: () => void;
}

type ViewMode = 'list' | 'calendar';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(cents: CentsAmount): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Convert an invoice to a DueDate object
 */
function invoiceToDueDate(invoice: {
  id: string;
  customerId?: string;
  customerName: string;
  dueDate: string;
  grossTotal: number;
  paidAmount: number;
  status: string;
}): DueDate {
  const now = new Date();
  const dueDate = new Date(invoice.dueDate);
  const isOverdue = dueDate < now && invoice.status !== 'paid' && invoice.status !== 'cancelled';
  const isPartial = invoice.paidAmount > 0 && invoice.paidAmount < invoice.grossTotal;

  let status: DueDateStatus;
  if (invoice.status === 'paid') {
    status = 'paid';
  } else if (isPartial) {
    status = 'partial';
  } else if (isOverdue) {
    status = 'overdue';
  } else {
    status = 'pending';
  }

  return {
    id: `due-${invoice.id}`,
    invoiceId: invoice.id,
    customerId: invoice.customerId || '',
    customerName: invoice.customerName,
    invoiceNumber: invoice.id.toUpperCase().replace('INV-', 'INV-'),
    dueDate: invoice.dueDate,
    amount: invoice.grossTotal,
    paidAmount: invoice.paidAmount,
    status,
    reminderSent: false,
  };
}

// =============================================================================
// CONSTANTS
// =============================================================================

const STATUS_LABELS: Record<DueDateStatus, string> = {
  pending: 'In scadenza',
  overdue: 'Scaduto',
  paid: 'Pagato',
  partial: 'Parziale',
};

// =============================================================================
// COMPONENTS
// =============================================================================

function StatusBadge({ status }: { status: DueDateStatus }) {
  return (
    <span>
      {STATUS_LABELS[status]}
    </span>
  );
}

interface DueDateCardProps {
  dueDate: DueDate;
  /** Linked items count per action type */
  linkedCounts?: LinkedCounts;
  onClick?: (dueDate: DueDate) => void;
  onActionClick?: (actionType: ActionType, dueDate: DueDate) => void;
  onRecordPayment: (dueDate: DueDate) => void;
}

function DueDateCard({
  dueDate,
  linkedCounts = {},
  onClick,
  onActionClick,
  onRecordPayment,
}: DueDateCardProps) {
  const days = getDaysFromDueDate(dueDate.dueDate);
  const isOverdue = days < 0;
  const remaining = dueDate.amount - dueDate.paidAmount;

  const getDaysLabel = () => {
    if (days === 0) return 'Scade oggi';
    if (days === 1) return 'Scade domani';
    if (days === -1) return 'Scaduto ieri';
    if (days > 0) return `Tra ${days} giorni`;
    return `${Math.abs(days)} giorni fa`;
  };

  const getStatusColor = () => {
    if (dueDate.status === 'paid') return '#16a34a';
    if (isOverdue) return '#dc2626';
    if (days <= 7) return '#f59e0b';
    return '#6b7280';
  };

  return (
    <div
      className="accounting-card--start"
      onClick={() => onClick?.(dueDate)}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Status indicator */}
      <div
        className="accounting-icon--md accounting-flex--center"
        style={{ color: getStatusColor() }}
      >
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {isOverdue ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          ) : dueDate.status === 'paid' ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          )}
        </svg>
      </div>

      {/* Info */}
      <div className="accounting-content" style={{ flex: 1 }}>
        <div className="accounting-flex--row accounting-flex--gap-sm" style={{ alignItems: 'center' }}>
          <span style={{ fontWeight: 500 }}>{dueDate.customerName}</span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 500,
            backgroundColor: isOverdue ? '#fef2f2' : dueDate.status === 'paid' ? '#dcfce7' : '#fef3c7',
            color: isOverdue ? '#dc2626' : dueDate.status === 'paid' ? '#16a34a' : '#92400e',
          }}>
            {STATUS_LABELS[dueDate.status]}
          </span>
        </div>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {dueDate.invoiceNumber} | Scadenza: {formatDate(dueDate.dueDate)}
        </div>
        <div style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: getStatusColor(),
        }}>
          {getDaysLabel()}
        </div>
      </div>

      {/* Amount */}
      <div className="accounting-content--right" style={{ marginRight: '1rem', textAlign: 'right' }}>
        <div style={{ fontWeight: 600, fontSize: '1rem' }}>{formatCurrency(remaining)}</div>
        {dueDate.paidAmount > 0 && (
          <div style={{ fontSize: '0.75rem', color: '#16a34a' }}>
            Pagato: {formatCurrency(dueDate.paidAmount)}
          </div>
        )}
      </div>

      {/* Fast Actions */}
      {dueDate.status !== 'paid' && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
        >
          {/* Special "Incassa" button */}
          <button
            onClick={() => onRecordPayment(dueDate)}
            title="Registra pagamento"
            style={{
              padding: '6px 10px',
              border: 'none',
              borderRadius: '4px',
              background: '#dcfce7',
              color: '#16a34a',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginRight: '4px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Incassa
          </button>

          {/* Fast Actions with counters */}
          <FastActions
            entityId={dueDate.id}
            entityName={dueDate.customerName}
            actions={['call', 'email', 'message', 'note']}
            linkedCounts={linkedCounts}
            onActionClick={(actionType) => onActionClick?.(actionType, dueDate)}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function DueDatesPage({ onNavigate, onBack }: DueDatesPageProps) {
  const { invoices, loadInvoices, loading } = useAccountingStore();
  const [statusFilter, setStatusFilter] = useState<DueDateStatus | 'all'>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Load invoices on mount
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Convert invoices to due dates (exclude paid and cancelled)
  const dueDates = useMemo(() => {
    return invoices
      .filter((inv) => inv.status !== 'paid' && inv.status !== 'cancelled')
      .map(invoiceToDueDate);
  }, [invoices]);

  const filteredDueDates = useMemo(() => {
    const filtered = dueDates.filter(
      (d) => statusFilter === 'all' || d.status === statusFilter
    );
    // Sort by due date, overdue first
    return filtered.sort((a, b) => {
      const daysA = getDaysFromDueDate(a.dueDate);
      const daysB = getDaysFromDueDate(b.dueDate);
      return daysA - daysB;
    });
  }, [dueDates, statusFilter]);

  const stats = useMemo(() => {
    const overdue = dueDates.filter((d) => d.status === 'overdue');
    const pending = dueDates.filter((d) => d.status === 'pending' || d.status === 'partial');
    return {
      overdueCount: overdue.length,
      overdueAmount: overdue.reduce((sum, d) => sum + (d.amount - d.paidAmount), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, d) => sum + (d.amount - d.paidAmount), 0),
    };
  }, [dueDates]);

  const handleSendReminder = (dueDate: DueDate) => {
    console.log('Send reminder for:', dueDate.invoiceNumber);
  };

  const handleRecordPayment = (dueDate: DueDate) => {
    console.log('Record payment for:', dueDate.invoiceNumber);
  };

  return (
    <div className="accounting-page accounting-page--narrow">
      {/* Stats */}
      <div className="accounting-grid--stats">
        <div className="accounting-stat--wide">
          <div>Scaduto</div>
          <div>{formatCurrency(stats.overdueAmount)}</div>
          <div>{stats.overdueCount} fattur{stats.overdueCount === 1 ? 'a' : 'e'}</div>
        </div>
        <div className="accounting-stat--wide">
          <div>In scadenza</div>
          <div>{formatCurrency(stats.pendingAmount)}</div>
          <div>{stats.pendingCount} fattur{stats.pendingCount === 1 ? 'a' : 'e'}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="accounting-toolbar">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DueDateStatus | 'all')}
        >
          <option value="all">Tutti gli stati</option>
          <option value="overdue">Scaduti</option>
          <option value="pending">In scadenza</option>
          <option value="partial">Parziali</option>
        </select>
        <div className="accounting-toolbar__spacer" />
        <div className="accounting-tabs--toggle">
          <button onClick={() => setViewMode('list')}>
            Lista
          </button>
          <button onClick={() => setViewMode('calendar')}>
            Calendario
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="accounting-empty">
          <p>Caricamento scadenze...</p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="accounting-list--gap-lg">
          {filteredDueDates.length === 0 ? (
            <div className="accounting-empty">
              <svg
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p>Nessuna scadenza trovata</p>
            </div>
          ) : (
            filteredDueDates.map((dueDate) => {
              // TODO: Fetch linked counts from LinkManager when available
              const linkedCounts: LinkedCounts = {};

              return (
                <DueDateCard
                  key={dueDate.id}
                  dueDate={dueDate}
                  linkedCounts={linkedCounts}
                  onClick={(d) => {
                    console.log('Open due date detail:', d.id);
                    // TODO: Open due date detail/invoice view
                  }}
                  onActionClick={(actionType, d) => {
                    console.log('Fast action clicked:', actionType, 'on due date:', d.id);
                    // TODO: Open LinkedActionsPanel for this action type
                  }}
                  onRecordPayment={handleRecordPayment}
                />
              );
            })
          )}
        </div>
      ) : (
        <div className="accounting-empty">
          <p>Vista calendario - Da implementare</p>
        </div>
      )}
    </div>
  );
}

export type { DueDatesPageProps };
